import { useWindowSize } from '@/hooks/helpers/useWindowSize'
import {
	Drawer,
	DrawerClose,
	DrawerContent,
	DrawerHeader,
	DrawerTitle,
	DrawerDescription,
	DrawerFooter,
	DrawerTrigger
} from './Drawer'
import {
	DialogContent,
	Dialog,
	DialogTrigger,
	DialogClose,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle
} from './Dialog'
import { createContext, useEffect, useId, useRef, useState } from 'react'
import React from 'react'
import { cn } from '@/lib/utils'
import { usePrevious } from '@/hooks/usePrevious'

interface ResponsiveDialogProps {
	open?: boolean
	onOpenChange?: (open: boolean) => void
	children: React.ReactNode
}

interface ChildProps {
	className?: string
	asChild?: true
	children: React.ReactNode
}

type UseResponsiveDialogProps = {
	/**
	 * If true, the props will contain a key that will reset the modal when it closes. It's important to destructure the key prop and avoid spreading it in the component.
	 */
	resetOnClose?: boolean
	onOpenChange?: (open: boolean) => void
	/**
	 * Callback that is called when the modal animation ends. Only called on mobile.
	 * @param open - The current state of the modal
	 */
	onAnimationEnd?: (open: boolean) => void
}

function useResponsiveDialog({ resetOnClose, onOpenChange, onAnimationEnd }: UseResponsiveDialogProps = {}) {
	const [isOpen, setIsOpen] = useState(false)
	const id = useId()
	const [key, setKey] = useState(id)
	const { isMobile } = useWindowSize()
	const previousOpen = usePrevious(isOpen)

	const open = () => setIsOpen(true)
	const close = () => setIsOpen(false)

	useEffect(() => {
		if (previousOpen !== undefined && previousOpen !== isOpen) {
			onOpenChange?.(isOpen)
		}
	}, [isOpen, previousOpen, onOpenChange])

	function updateKey() {
		setKey(`${id}-${Date.now()}`)
	}

	return [
		{
			open: isOpen,
			onOpenChange: (open: boolean) => {
				setIsOpen(open)
				if (!open && resetOnClose && !isMobile) {
					updateKey()
				}
			},
			...(isMobile && {
				onAnimationEnd: (open: boolean) => {
					if (!open && resetOnClose) {
						updateKey()
					}

					onAnimationEnd?.(open)
				}
			}),
			...(resetOnClose && { key })
		},
		open,
		close
	] as const
}

const ResponsiveDialogContext = createContext<{ isMobile: boolean }>({
	isMobile: false
})

const useResponsiveDialogContext = () => {
	const context = React.useContext(ResponsiveDialogContext)
	if (!context) {
		throw new Error(
			'Must be used within a `ResponsiveDialog` component. Wrap a parent component in `<ResponsiveDialog>` to fix this error.'
		)
	}
	return context
}

const ResponsiveDialog = ({ children, ...props }: ResponsiveDialogProps) => {
	const { isMobile } = useWindowSize()
	const ResponsiveDialogComp = isMobile ? Drawer : Dialog

	return (
		<ResponsiveDialogContext.Provider value={{ isMobile }}>
			<ResponsiveDialogComp {...props} {...(isMobile && { repositionInputs: false })}>
				{children}
			</ResponsiveDialogComp>
		</ResponsiveDialogContext.Provider>
	)
}

const ResponsiveDialogTrigger = ({ className, children, ...props }: ChildProps) => {
	const { isMobile } = useResponsiveDialogContext()
	const ResponsiveDialogTriggerComp = isMobile ? DrawerTrigger : DialogTrigger

	return (
		<ResponsiveDialogTriggerComp className={className} {...props}>
			{children}
		</ResponsiveDialogTriggerComp>
	)
}

const ResponsiveDialogClose = ({ className, children, ...props }: ChildProps) => {
	const { isMobile } = useResponsiveDialogContext()
	const ResponsiveDialogCloseComp = isMobile ? DrawerClose : DialogClose

	return (
		<ResponsiveDialogCloseComp className={className} {...props}>
			{children}
		</ResponsiveDialogCloseComp>
	)
}

type ResponsiveDialogContentProps = ChildProps &
	Pick<React.ComponentPropsWithoutRef<typeof DialogContent>, 'onInteractOutside' | 'defaultCloseButton'>

const ResponsiveDialogContent = ({
	className,
	children,
	defaultCloseButton = true,
	...props
}: ResponsiveDialogContentProps) => {
	const { isMobile } = useResponsiveDialogContext()
	const ResponsiveDialogContentComp = isMobile ? DrawerContent : DialogContent

	return (
		<ResponsiveDialogContentComp
			className={cn('py-8 px-6 md:p-10 rounded-t-4xl max-h-screen md:rounded-4xl', className)}
			defaultCloseButton={defaultCloseButton}
			{...props}
		>
			{children}
		</ResponsiveDialogContentComp>
	)
}

const ResponsiveDialogDescription = ({ className, children, ...props }: ChildProps) => {
	const { isMobile } = useResponsiveDialogContext()
	const ResponsiveDialogDescriptionComp = isMobile ? DrawerDescription : DialogDescription

	return (
		<ResponsiveDialogDescriptionComp className={className} {...props}>
			{children}
		</ResponsiveDialogDescriptionComp>
	)
}

const ResponsiveDialogHeader = ({ className, children, ...props }: ChildProps) => {
	const { isMobile } = useResponsiveDialogContext()
	const ResponsiveDialogHeaderComp = isMobile ? DrawerHeader : DialogHeader

	return (
		<ResponsiveDialogHeaderComp className={className} {...props}>
			{children}
		</ResponsiveDialogHeaderComp>
	)
}

const ResponsiveDialogTitle = ({ className, children, ...props }: ChildProps) => {
	const { isMobile } = useResponsiveDialogContext()
	const ResponsiveDialogTitleComp = isMobile ? DrawerTitle : DialogTitle

	return (
		<ResponsiveDialogTitleComp className={className} {...props}>
			{children}
		</ResponsiveDialogTitleComp>
	)
}

const ResponsiveDialogFooter = ({ className, children, ...props }: ChildProps) => {
	const { isMobile } = useResponsiveDialogContext()
	const ResponsiveDialogFooterComp = isMobile ? DrawerFooter : DialogFooter

	return (
		<ResponsiveDialogFooterComp className={className} {...props}>
			{children}
		</ResponsiveDialogFooterComp>
	)
}

export {
	ResponsiveDialog,
	ResponsiveDialogTrigger,
	ResponsiveDialogClose,
	ResponsiveDialogContent,
	ResponsiveDialogDescription,
	ResponsiveDialogHeader,
	ResponsiveDialogTitle,
	ResponsiveDialogFooter,
	useResponsiveDialog
}
export type { ResponsiveDialogProps, ChildProps }
